import { create } from 'zustand';
import type { LearnerState } from '@/lib/types';
import { createLearnerState, getLearnerState, updateLearnerState } from '@/lib/api';

// ── localStorage helpers ──

const SESSION_KEY_PREFIX = 'itari_session_';

/** Build a localStorage key from moduleId + educator name. */
function sessionKey(moduleId: string, educatorName?: string): string {
  const slug = (educatorName || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  return slug
    ? `${SESSION_KEY_PREFIX}${moduleId}_${slug}`
    : `${SESSION_KEY_PREFIX}${moduleId}`;
}

function storeSession(moduleId: string, learnerId: string, educatorName?: string) {
  try {
    const key = sessionKey(moduleId, educatorName);
    localStorage.setItem(key, JSON.stringify({ learnerId, educatorName }));
  } catch { /* storage full / private mode – non-fatal */ }
}

function getStoredSession(moduleId: string, educatorName?: string): { learnerId: string; educatorName?: string } | null {
  try {
    const key = sessionKey(moduleId, educatorName);
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearStoredSession(moduleId: string, educatorName?: string) {
  try {
    const key = sessionKey(moduleId, educatorName);
    localStorage.removeItem(key);
  } catch { /* ignore */ }
}

// ── Store ──

interface LearnerStateStore {
  state: LearnerState | null;
  loading: boolean;
  error: Error | null;

  /**
   * Try to resume an existing session for the given module + educator name.
   * Returns `true` if a valid session was found and loaded, `false` otherwise.
   */
  resume: (moduleId: string, educatorName?: string) => Promise<boolean>;

  /** Create a brand-new learner session. */
  initialize: (moduleId: string, educatorName?: string) => Promise<void>;

  load: (learnerId: string) => Promise<void>;
  updatePosition: (position: LearnerState['currentPosition']) => Promise<void>;
  updateNotepad: (partId: string, prompt: string, answer: string) => void;
  saveNotepad: (partId: string) => Promise<void>;
}

export const useLearnerState = create<LearnerStateStore>((set, get) => ({
  state: null,
  loading: false,
  error: null,

  resume: async (moduleId: string, educatorName?: string) => {
    const stored = getStoredSession(moduleId, educatorName);
    if (!stored?.learnerId) return false;

    set({ loading: true, error: null });
    try {
      const state = await getLearnerState(stored.learnerId);
      set({ state, loading: false });
      return true;
    } catch {
      // Session no longer exists on the backend – clean up
      clearStoredSession(moduleId, educatorName);
      set({ loading: false, error: null });
      return false;
    }
  },

  initialize: async (moduleId: string, educatorName?: string) => {
    set({ loading: true, error: null });
    try {
      const state = await createLearnerState(moduleId, educatorName);
      storeSession(moduleId, state.id, educatorName);
      set({ state, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error : new Error('Failed to initialize'),
        loading: false,
      });
    }
  },

  load: async (learnerId: string) => {
    set({ loading: true, error: null });
    try {
      const state = await getLearnerState(learnerId);
      set({ state, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error : new Error('Failed to load state'),
        loading: false,
      });
    }
  },

  updatePosition: async (position: LearnerState['currentPosition']) => {
    const { state } = get();
    if (!state) {
      throw new Error('No learner state initialized');
    }

    set({ loading: true, error: null });
    try {
      const updatedState = await updateLearnerState({
        learnerId: state.id,
        moduleId: state.moduleId,
        position,
      });
      set({ state: updatedState, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error : new Error('Failed to update position'),
        loading: false,
      });
    }
  },

  // Optimistic local update — saves to Zustand store immediately (no API call)
  updateNotepad: (partId: string, prompt: string, answer: string) => {
    const { state } = get();
    if (!state) return;

    const updatedState = {
      ...state,
      parts: {
        ...state.parts,
        [partId]: {
          ...state.parts[partId],
          notepad: { prompt, answer },
        },
      },
    };
    set({ state: updatedState });
  },

  // Persist notepad to backend
  saveNotepad: async (partId: string) => {
    const { state } = get();
    if (!state) return;

    const notepad = state.parts[partId]?.notepad;
    if (!notepad) return;

    try {
      await updateLearnerState({
        learnerId: state.id,
        moduleId: state.moduleId,
        notepadUpdate: {
          partId,
          prompt: notepad.prompt,
          answer: notepad.answer,
        },
      });
    } catch (error) {
      console.error('Failed to save notepad:', error);
    }
  },
}));
